import React, {
  FormEventHandler,
  ReactNode,
  RefObject,
  ChangeEvent, KeyboardEvent, useRef, useState
} from 'react';

import { Visibility, VisibilityOff } from '@groww-tech-pvt/icon-store/mi';
import { isEmpty } from '@groww-tech-pvt/ella';

import './styles.css';

const numberRegex = /^[\d\s]*\.?[\d\s]*$/; // Regex to accept numbers, spaces, and optional decimal point
const textRegex = /^[a-zA-Z\s]*$/; // Regex to accept only chars and letter not number and spaces

const InputField = (props: InputFieldProps) => {
  const [focused, setFocused] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const {
    onKeyDown = () => { },
    onKeyUp = () => { },
    onCopy = () => { },
    onPaste = () => { },
    error,
    size = 'medium',
    label,
    PrefixComponent,
    SuffixComponent,
    className = '',
    onEnterPress,
    onChange,
    inputRef,
    disabled,
    type,
    value,
    placeholder = type === 'date' ? 'DD/MM/YYYY' : undefined,
    ...rest
  } = props;

  const isPasswordType = type === 'password';
  const passwordSuffix = isPasswordType
    ? () => (
      <span
        className='cursor-pointer contentSecondary ml-2 flex items-center'
        onClick={() => setIsPasswordVisible(v => !v)}
      >
        {isPasswordVisible ? <VisibilityOff size={18} /> : <Visibility size={18} />}
      </span>
    )
    : null;

  const isDateType = type === 'date';
  // Start as 'text' so placeholder is visible; switch to 'date' on focus for the picker
  const [dateInputType, setDateInputType] = useState<'text' | 'date'>('text');

  const localInputRef = useRef<HTMLInputElement>(null);
  const inputRefrence = inputRef || localInputRef;

  const handleOnKeyUp = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && onEnterPress) onEnterPress(e);
    onKeyUp(e);
  };

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    if (isPasswordType && !isPasswordVisible) {
      const actual = String(value || '');
      let newActual: string;

      if (inputValue.length > actual.length) {
        // Characters added at the end                                                                                                                    
        newActual = actual + inputValue.slice(actual.length);
      } else {
        // Characters deleted                                                                                                                             
        newActual = actual.slice(0, inputValue.length);
      }

      const syntheticEvent = Object.assign({}, e, {
        target: Object.assign({}, e.target, { value: newActual })
      });

      onChange(syntheticEvent as ChangeEvent<HTMLInputElement>);
      return;
    }
    if (type === 'number') {
      const allowedInputLength = props?.maxLength || 1000;
      if (!numberRegex.test(inputValue) || inputValue.length > allowedInputLength) {
        e.preventDefault();
        return;
      }
    }
    if (type === 'text') {
      if (!textRegex.test(inputValue)) {
        e.preventDefault();
        return;
      }
    }
    onChange(e);
  };

  const getDisplayValue = () => {

    if (isPasswordType && !isPasswordVisible) {
      return 'X'.repeat(String(value || '').length);
    }


    if (isDateType && dateInputType === 'text' && value) {
      const parts = String(value).split('-');
      return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : value;
    }
    return value;
  }
  // When blurred, reformat stored YYYY-MM-DD → DD/MM/YYYY for display
  const displayValue = getDisplayValue();


  const computedClass = calculateInputClass(size);
  let inputElementClass = 'onb-input !border-none flex-grow p-0 no-spinner ';
  inputElementClass += 'caret-color-text-accent ';
  inputElementClass += 'focus:outline-none focus:border-none ';

  if (disabled) {
    inputElementClass += 'contentDisabled cursor-not-allowed ';
  } else if (isEmpty(value)) {
    inputElementClass += 'contentSecondary!'
  } else {
    inputElementClass += 'contentPrimary';
  }

  let inputBorderClass = 'border rounded';

  if (error) {
    inputBorderClass += ' border-negative';
  } else if (focused) {
    inputBorderClass += ' border-neutral ring-1 ring-primary';
  } else {
    inputBorderClass += ' border-primary';
  }

  return (
    <div>
      {label && (
        <div className='contentSecondary mb-1 text-left bodyBaseHeavy'>{label}</div>
      )}

      <div className={`flex items-center px-4 py-3 w-full ${inputBorderClass}`}>
        {PrefixComponent && <span className='h-full'>{PrefixComponent()}</span>}
        <input
          className={`${inputElementClass} ${computedClass} ${className}`}
          ref={inputRefrence}
          onCopy={onCopy}
          onPaste={onPaste}
          onKeyUp={handleOnKeyUp}
          onKeyDown={onKeyDown}
          onFocus={() => {
            if (isDateType) setDateInputType('date');
            setFocused(true);
          }}
          onBlur={() => {
            if (isDateType) setDateInputType('text');
            setFocused(false);
          }}
          onChange={handleOnChange}
          disabled={disabled}
          type={isDateType ? dateInputType : isPasswordType ? 'text' : (type === 'number' ? 'text' : type)} inputMode={type === 'number' ? 'numeric' : undefined}
          value={displayValue}
          placeholder={placeholder}
          spellCheck={false}
          autoComplete="off"
          {...rest}
        />
        {(SuffixComponent ?? passwordSuffix) && <span className='h-full'>{(SuffixComponent ?? passwordSuffix)!()}</span>}
      </div>

      {!isEmpty(error) && (
        <div className='contentNegative bodyBase min-h-6 mt-2 text-center'>
          {error ? error : ''}
        </div>
      )}
    </div>
  );
};

const calculateInputClass = (size: InputFieldProps['size']): string => {
  switch (size) {
    case 'small':
      return 'bodyBase ';

    case 'medium':
      return 'bodyLarge';

    case 'large':
      return 'bodyLarge';

    default:
      return 'bodyLarge';
  }
};

export type InputFieldProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'size'
> &
  _InputFieldProps;

type _InputFieldProps = {
  disabled?: boolean;
  error?: string | boolean;
  SuffixComponent?: (() => ReactNode) | null;
  PrefixComponent?: (() => ReactNode) | null;
  label?: string;
  variant?: 'default' | 'exclusive' | 'unstyled';
  onChange: FormEventHandler<HTMLInputElement>;
  size?: 'small' | 'medium' | 'large';
  onEnterPress?: (e: KeyboardEvent<HTMLInputElement>) => void;
  inputRef?: RefObject<HTMLInputElement>;
};

export default InputField;

